import { useState } from 'react';
import { Sparkle, User } from '@phosphor-icons/react';
import { featureFlagInputSchema, FLAG_AUDIENCES, type FlagAudience } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { formatStamp } from '../../lib/format-edited';
import { initialsOf } from '../../lib/initials';
import { useQuery } from '@tanstack/react-query';
import type { FeatureFlagRecord } from '@mybusinessmate/schema';
import { flagHistoryQuery } from '../../lib/workspace-api';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Button } from '../ui/button';
import { Drawer } from '../ui/dialog';
import { Input } from '../ui/input';
import { TagInput } from '../ui/tag-input';
import { TextArea } from '../ui/text-area';
import { Toggle } from '../ui/toggle';
import { ErrorState } from '../ui/state-views';
import { SkeletonRows } from '../ui/skeleton';

const AUDIENCE_LABEL: Record<FlagAudience, string> = {
  everyone: 'Everyone',
  share: 'A share of clients',
  businesses: 'Specific businesses',
  staff: 'Staff only',
};

export interface FlagEdit {
  enabled: boolean;
  audience: FlagAudience;
  sharePercent: number;
  businessIds: string[];
  alwaysOnForStaff: boolean;
  reason?: string;
}

function Radio({ label, selected, onSelect }: { label: string; selected: boolean; onSelect: () => void }) {
  return (
    <label
      className={cx(
        'flex cursor-pointer items-center gap-(--mbm-space-3) rounded-(--mbm-radius-sm) border px-(--mbm-space-4) py-(--mbm-space-3)',
        selected ? 'border-transparent bg-surface-tint' : 'border-border-default bg-surface-card',
        'has-[:focus-visible]:shadow-(--mbm-shadow-focus-ring)',
      )}
    >
      <input type="radio" name="flag-audience" checked={selected} onChange={onSelect} className="peer sr-only" />
      <span
        aria-hidden
        className={cx(
          'flex size-(--mbm-size-radio) shrink-0 items-center justify-center rounded-full border-[1.5px]',
          selected ? 'border-brand-primary' : 'border-border-strong',
        )}
      >
        {selected ? <span className="size-(--mbm-space-3) rounded-full bg-brand-primary" /> : null}
      </span>
      <span className="text-label-medium text-text-primary">{label}</span>
    </label>
  );
}

function EditForm({
  flag,
  onCancel,
  onSave,
  onHistory,
}: {
  flag: FeatureFlagRecord;
  onCancel: () => void;
  onSave: (edit: FlagEdit) => void;
  onHistory: () => void;
}) {
  const [enabled, setEnabled] = useState(flag.enabled);
  const [audience, setAudience] = useState<FlagAudience>(flag.audience);
  const [share, setShare] = useState(String(flag.sharePercent));
  const [businesses, setBusinesses] = useState<string[]>(flag.businessIds);
  const [staffOn, setStaffOn] = useState(flag.alwaysOnForStaff);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ share?: string; businesses?: string }>({});

  const sharePercent = Number(share);
  // A reason is worth recording when the flag gets riskier for clients or switches off.
  const asksForReason =
    (flag.enabled && !enabled) ||
    (audience === 'share' && sharePercent > flag.sharePercent && flag.audience === 'share');

  const submit = () => {
    const parsed = featureFlagInputSchema.safeParse({
      enabled,
      audience,
      sharePercent: Number.isFinite(sharePercent) ? sharePercent : 0,
      businessIds: businesses,
      alwaysOnForStaff: staffOn,
      reason: reason || undefined,
    });
    if (!parsed.success) {
      const next: typeof errors = {};
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'sharePercent') next.share ??= issue.message;
        if (issue.path[0] === 'businessIds') next.businesses ??= issue.message;
      }
      setErrors(next);
      return;
    }
    onSave({
      enabled: parsed.data.enabled,
      audience: parsed.data.audience,
      sharePercent: parsed.data.sharePercent,
      businessIds: parsed.data.businessIds,
      alwaysOnForStaff: parsed.data.alwaysOnForStaff,
      reason: parsed.data.reason,
    });
  };

  return (
    <div className="flex h-full flex-col gap-(--mbm-space-5)">
      <div className="flex flex-1 flex-col gap-(--mbm-space-5)">
        <div className="flex items-center gap-(--mbm-space-4)">
          <span className="flex w-(--mbm-size-icon-tile) shrink-0 justify-center text-text-secondary">
            <Icon icon={Sparkle} size="md" />
          </span>
          <span className="flex flex-1 flex-col">
            <span className="text-label-large text-text-primary">{enabled ? 'Flag is on' : 'Flag is off'}</span>
            <span className="text-body-small text-text-secondary">
              {enabled
                ? 'Turn off to hide it from everyone right away'
                : 'Turn on to release it to the people you pick'}
            </span>
          </span>
          <Toggle aria-label="Flag is on" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <fieldset className="flex flex-col gap-(--mbm-space-3)">
          <legend className="pb-(--mbm-space-3) text-heading-h4 text-text-primary">Who gets it</legend>
          {FLAG_AUDIENCES.map((a) => (
            <Radio key={a} label={AUDIENCE_LABEL[a]} selected={audience === a} onSelect={() => setAudience(a)} />
          ))}
        </fieldset>

        {audience === 'share' ? (
          <Input
            variant="filled"
            label="Share of clients"
            inputMode="numeric"
            trailingSlot={<span className="text-body-medium text-text-secondary">%</span>}
            value={share}
            error={errors.share}
            helperText="Picked at random, and the same clients stay in when you raise it."
            onChange={(e) => {
              setShare(e.target.value.replace(/\D/g, '').slice(0, 3));
              setErrors({});
            }}
          />
        ) : null}

        {audience === 'businesses' ? (
          <div className="flex flex-col gap-(--mbm-space-2)">
            {/* TODO(api): picking by name needs the businesses list endpoint; for now businesses are added by id. */}
            <TagInput
              label="Businesses"
              value={businesses}
              placeholder="Add a business id and press Enter"
              onChange={(v) => {
                setBusinesses(v);
                setErrors({});
              }}
            />
            {errors.businesses ? (
              <p role="alert" className="text-body-small text-feedback-error">
                {errors.businesses}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center gap-(--mbm-space-4)">
          <span className="flex w-(--mbm-size-icon-tile) shrink-0 justify-center text-text-secondary">
            <Icon icon={User} size="md" />
          </span>
          <span className="flex flex-1 flex-col">
            <span className="text-label-large text-text-primary">Always on for staff</span>
            <span className="text-body-small text-text-secondary">So the team can test it before clients see it</span>
          </span>
          <Toggle aria-label="Always on for staff" checked={staffOn} onCheckedChange={setStaffOn} />
        </div>

        {asksForReason ? (
          <TextArea
            variant="filled"
            label="Reason · saved in the change history"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <button
          type="button"
          onClick={onHistory}
          className="rounded-(--mbm-radius-xs) text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          View change history
        </button>
        <div className="flex gap-(--mbm-space-2)">
          <Button variant="secondary" size="compact" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="compact" onClick={submit}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

/** "Edit flag" drawer (Figma A16.10): on/off, who gets it, and the always-on-for-staff switch. */
export function EditFlagDrawer({
  flag,
  onClose,
  onSave,
  onHistory,
}: {
  flag: FeatureFlagRecord | undefined;
  onClose: () => void;
  onSave: (edit: FlagEdit) => void;
  onHistory: () => void;
}) {
  return (
    <Drawer open={Boolean(flag)} onClose={onClose} title={flag?.name ?? 'Edit flag'} description={flag?.key}>
      {flag ? <EditForm key={flag.key} flag={flag} onCancel={onClose} onSave={onSave} onHistory={onHistory} /> : null}
    </Drawer>
  );
}

/** "Change history" drawer: who changed what, newest first. */
export function FlagHistoryDrawer({ flag, onClose }: { flag: FeatureFlagRecord | undefined; onClose: () => void }) {
  const history = useQuery({ ...flagHistoryQuery(flag?.key ?? ''), enabled: Boolean(flag) });
  return (
    <Drawer
      open={Boolean(flag)}
      onClose={onClose}
      title={flag?.name ?? 'Change history'}
      description={flag ? `Change history · ${flag.key}` : undefined}
    >
      {history.isLoading ? <SkeletonRows rows={3} label="Loading the history" /> : null}
      {history.isError ? (
        <ErrorState
          title="Couldn’t load the history"
          className="py-(--mbm-space-5)"
          onRetry={() => void history.refetch()}
        />
      ) : null}
      {history.data && history.data.length === 0 ? (
        <p className="text-body-medium text-text-secondary">No changes yet. Edits show up here with who made them.</p>
      ) : null}
      <ol className="flex flex-col gap-(--mbm-space-6)">
        {history.data?.map((entry) => (
          <li key={entry.id} className="flex gap-(--mbm-space-3)">
            <Avatar initials={initialsOf(entry.by)} size="md" className="bg-brand-primary" />
            <div className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
              <span className="text-label-medium text-text-primary">
                {entry.by} {entry.title}
              </span>
              {entry.detail ? <span className="text-body-small text-text-secondary">{entry.detail}</span> : null}
              <span className="text-body-xsmall text-text-tertiary">{formatStamp(entry.at)}</span>
            </div>
          </li>
        ))}
      </ol>
    </Drawer>
  );
}
